import { useEffect, useMemo, useState } from "react";
import type { Vehicle } from "./useVehicleStream";
import {
  buildRouteProjection,
  type ProjectedStop,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";
import "./NYCCountdown.css";

// NYC MTA-style countdown clock: black panel, amber LED text for
// destinations and minutes, green LED for the wall clock and the
// "min" digit. One station at a time, with two stacked direction
// sections and a scrolling safety-message marquee.

const STALE_AFTER_SEC = 120;
const CRUISE_M_PER_S = 13.4;
const APPROACH_HORIZON_M = 25_000;
const MAX_TRAINS_PER_DIR = 4;
const ROTATE_INTERVAL_SEC = 20;

// Headsign keywords that map a vehicle to one of the two compass
// sections of the panel. "Uptown" = north-ish (Lynnwood, Redmond),
// "Downtown" = south-ish (Federal Way, Angle Lake).
const UPTOWN_HEADSIGNS = ["lynnwood", "redmond"];
const DOWNTOWN_HEADSIGNS = ["federal way", "angle lake"];

const MARQUEE_MESSAGES = [
  "STAND CLEAR OF THE CLOSING DOORS, PLEASE.",
  "THERE IS A NORTHBOUND 1 LINE TRAIN APPROACHING THE STATION.",
  "THANK YOU FOR RIDING WITH US.",
  "IF YOU SEE SOMETHING, SAY SOMETHING.",
  "PLEASE OFFER YOUR SEAT TO THE ELDERLY OR DISABLED.",
  "THE NEXT NORTHBOUND TRAIN IS NOW ARRIVING.",
];

type Props = {
  vehicles: Vehicle[];
  connected: boolean;
  /** True when the parent is showing a historical (scrubbed) snapshot. */
  historical?: boolean;
};

type Direction = "uptown" | "downtown";

type StationOption = {
  routeId: string;
  routeShortName: string;
  routeColor: string;
  stop: ProjectedStop;
  /** Sign convention: distance increases in the "uptown" direction. */
  uptownIsForward: boolean;
};

type Train = {
  vehicleId: string;
  routeId: string;
  routeShortName: string;
  routeColor: string;
  headsign: string;
  etaSec: number;
  direction: Direction;
};

export function NYCCountdown({
  vehicles,
  connected,
  historical = false,
}: Props) {
  const [projections, setProjections] = useState<RouteProjection[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [autoRotate, setAutoRotate] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Wall clock tick. Frozen in historical mode so the LED time
  // reflects the scrubbed-to instant.
  useEffect(() => {
    if (historical) return;
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [historical]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        projs.sort((a, b) =>
          a.shortName.localeCompare(b.shortName, undefined, { numeric: true }),
        );
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stations: StationOption[] = useMemo(() => {
    if (!projections) return [];
    const out: StationOption[] = [];
    const seen = new Set<string>();
    for (const proj of projections) {
      // Decide which end of the polyline is "uptown" by looking for an
      // uptown-named terminus at one end of the sorted stops list.
      const stops = proj.stops;
      if (stops.length < 2) continue;
      const lastName = stops[stops.length - 1].name.toLowerCase();
      const firstName = stops[0].name.toLowerCase();
      const lastIsUptown = UPTOWN_HEADSIGNS.some((u) => lastName.includes(u));
      const firstIsUptown = UPTOWN_HEADSIGNS.some((u) => firstName.includes(u));
      const uptownIsForward = lastIsUptown
        ? true
        : firstIsUptown
          ? false
          : true;
      for (const s of stops) {
        const key = `${proj.routeId}::${s.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          routeId: proj.routeId,
          routeShortName: proj.shortName || proj.longName,
          routeColor: proj.color,
          stop: s,
          uptownIsForward,
        });
      }
    }
    return out;
  }, [projections]);

  // Default to a sensible mid-line station — prefer Westlake on the 1
  // Line, otherwise the median stop of the first route.
  useEffect(() => {
    if (selectedKey || stations.length === 0) return;
    const preferred =
      stations.find(
        (s) =>
          s.routeId === "40_100479" &&
          s.stop.name.toLowerCase().includes("westlake"),
      ) ||
      stations.find((s) => s.routeId === "40_100479") ||
      stations[Math.floor(stations.length / 2)];
    setSelectedKey(`${preferred.routeId}::${preferred.stop.id}`);
  }, [stations, selectedKey]);

  // Auto-rotate among stations on the same route every N seconds when
  // the toggle is on. Skipped while history is being scrubbed because
  // the countdowns wouldn't be live anyway.
  useEffect(() => {
    if (!autoRotate || historical || stations.length === 0) return;
    const id = window.setInterval(() => {
      setSelectedKey((cur) => {
        if (!cur) return stations[0] ? keyOf(stations[0]) : null;
        const idx = stations.findIndex((s) => keyOf(s) === cur);
        const next = stations[(idx + 1) % stations.length];
        return keyOf(next);
      });
    }, ROTATE_INTERVAL_SEC * 1000);
    return () => window.clearInterval(id);
  }, [autoRotate, historical, stations]);

  const selected = useMemo(
    () => stations.find((s) => keyOf(s) === selectedKey) || null,
    [stations, selectedKey],
  );

  const projByRoute = useMemo(() => {
    const m = new Map<string, RouteProjection>();
    for (const p of projections || []) m.set(p.routeId, p);
    return m;
  }, [projections]);

  const refSec = historical
    ? vehicles.reduce((m, v) => Math.max(m, v.ts || 0), 0) ||
      Math.floor(Date.now() / 1000)
    : Math.floor(Date.now() / 1000);

  const trains: Train[] = useMemo(() => {
    if (!selected) return [];
    const proj = projByRoute.get(selected.routeId);
    if (!proj) return [];
    const stationDist = selected.stop.distM;
    const out: Train[] = [];
    for (const v of vehicles) {
      if (!v.vehicle_id || v.route_id !== selected.routeId) continue;
      const headsign = v.trip_headsign || "";
      const dir = classifyDirection(headsign);
      if (!dir) continue;
      const ts = v.ts || refSec;
      if (refSec - ts > STALE_AFTER_SEC) continue;
      const distM = proj.project(v.lat, v.lon);
      if (distM == null) continue;

      // Remaining along-route distance to the station, signed so that
      // positive means "approaching from behind". The sign of a train
      // moving uptown depends on whether the polyline was digitized in
      // the uptown or downtown direction.
      const uptownSign = selected.uptownIsForward ? 1 : -1;
      const trainSign = dir === "uptown" ? uptownSign : -uptownSign;
      const remainingM = (stationDist - distM) * trainSign;
      if (remainingM < -200) continue;
      if (remainingM > APPROACH_HORIZON_M) continue;

      out.push({
        vehicleId: v.vehicle_id,
        routeId: v.route_id || selected.routeId,
        routeShortName:
          v.route_short_name || routeShortFor(v.route_id) || selected.routeShortName,
        routeColor: v.route_color
          ? `#${v.route_color}`
          : selected.routeColor,
        headsign,
        etaSec: Math.max(0, remainingM) / CRUISE_M_PER_S,
        direction: dir,
      });
    }
    out.sort((a, b) => a.etaSec - b.etaSec);
    return out;
  }, [vehicles, selected, projByRoute, refSec]);

  const uptownTrains = trains
    .filter((t) => t.direction === "uptown")
    .slice(0, MAX_TRAINS_PER_DIR);
  const downtownTrains = trains
    .filter((t) => t.direction === "downtown")
    .slice(0, MAX_TRAINS_PER_DIR);

  if (error) {
    return (
      <div className="nyc-empty">
        Couldn't load route geometry: {error}
      </div>
    );
  }
  if (!projections || !selected) {
    return <div className="nyc-empty">Loading route geometry…</div>;
  }

  return (
    <div className="nyc-wrap">
      <div className="nyc-toolbar">
        <label className="nyc-tool">
          <span className="nyc-tool-label">Station</span>
          <select
            className="nyc-select"
            value={selectedKey || ""}
            onChange={(e) => setSelectedKey(e.target.value)}
          >
            {groupedOptions(stations).map((group) => (
              <optgroup key={group.routeId} label={group.label}>
                {group.options.map((s) => (
                  <option key={keyOf(s)} value={keyOf(s)}>
                    {s.stop.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label className="nyc-tool nyc-tool-toggle">
          <input
            type="checkbox"
            checked={autoRotate}
            onChange={(e) => setAutoRotate(e.target.checked)}
            disabled={historical}
          />
          <span>Auto-rotate (20s)</span>
        </label>
        <div
          className={`nyc-status ${historical ? "history" : connected ? "ok" : "wait"}`}
        >
          {historical
            ? "◐ history"
            : connected
              ? "● live"
              : "○ waiting…"}
        </div>
      </div>

      <div className="nyc-panel" role="region" aria-label="NYC countdown clock">
        <div className="nyc-scanlines" aria-hidden />

        <header className="nyc-header">
          <div className="nyc-header-left">
            <RouteBullet
              shortName={selected.routeShortName}
              color={selected.routeColor}
              size="lg"
            />
            <div className="nyc-station-name">{selected.stop.name.toUpperCase()}</div>
          </div>
          <div className="nyc-clock">{formatClock(now)}</div>
        </header>

        <Section
          title="Uptown & The Bronx"
          subtitle={uptownSubtitle(selected)}
          trains={uptownTrains}
          fallbackColor={selected.routeColor}
          fallbackShort={selected.routeShortName}
          emptyLabel="NO TRAINS APPROACHING"
        />
        <Section
          title="Downtown & Brooklyn"
          subtitle={downtownSubtitle(selected)}
          trains={downtownTrains}
          fallbackColor={selected.routeColor}
          fallbackShort={selected.routeShortName}
          emptyLabel="NO TRAINS APPROACHING"
        />

        <Marquee messages={MARQUEE_MESSAGES} paused={historical} />
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  trains,
  fallbackColor,
  fallbackShort,
  emptyLabel,
}: {
  title: string;
  subtitle: string;
  trains: Train[];
  fallbackColor: string;
  fallbackShort: string;
  emptyLabel: string;
}) {
  return (
    <section className="nyc-section">
      <header className="nyc-section-header">
        <span className="nyc-section-title">{title.toUpperCase()}</span>
        <span className="nyc-section-sub">{subtitle.toUpperCase()}</span>
      </header>
      <ul className="nyc-rows">
        {trains.length === 0 ? (
          <li className="nyc-row nyc-row-empty">
            <span className="nyc-row-dest">{emptyLabel}</span>
          </li>
        ) : (
          trains.map((t, i) => (
            <li key={`${t.vehicleId}-${i}`} className="nyc-row">
              <RouteBullet
                shortName={t.routeShortName || fallbackShort}
                color={t.routeColor || fallbackColor}
                size="md"
              />
              <span className="nyc-row-dest">
                {formatHeadsign(t.headsign)}
              </span>
              <span className="nyc-row-eta">
                {formatEta(t.etaSec)}
              </span>
            </li>
          ))
        )}
      </ul>
    </section>
  );
}

function RouteBullet({
  shortName,
  color,
  size,
}: {
  shortName: string;
  color: string;
  size: "md" | "lg";
}) {
  return (
    <span
      className={`nyc-bullet nyc-bullet-${size}`}
      style={{ background: color }}
      aria-label={`Route ${shortName}`}
    >
      {shortName}
    </span>
  );
}

function Marquee({
  messages,
  paused,
}: {
  messages: string[];
  paused: boolean;
}) {
  // Repeat the message strip so the CSS animation can scroll continuously
  // without a visible seam.
  const joined = messages.join("   •   ");
  return (
    <div className={`nyc-marquee ${paused ? "is-paused" : ""}`}>
      <div className="nyc-marquee-track">
        <span className="nyc-marquee-chunk">{joined}</span>
        <span className="nyc-marquee-chunk" aria-hidden>
          {joined}
        </span>
      </div>
    </div>
  );
}

function classifyDirection(headsign: string): Direction | null {
  const h = headsign.toLowerCase();
  if (UPTOWN_HEADSIGNS.some((m) => h.includes(m))) return "uptown";
  if (DOWNTOWN_HEADSIGNS.some((m) => h.includes(m))) return "downtown";
  return null;
}

function uptownSubtitle(s: StationOption): string {
  return s.routeId === "40_2LINE"
    ? "Redmond / Lynnwood-bound"
    : "Lynnwood-bound";
}

function downtownSubtitle(s: StationOption): string {
  return s.routeId === "40_2LINE"
    ? "Westbound to S. Bellevue"
    : "Federal Way / Angle Lake-bound";
}

function formatHeadsign(h: string): string {
  if (!h) return "TRAIN";
  const cleaned = h.replace(/\s+/g, " ").trim();
  return `${cleaned.toUpperCase()}-BOUND`;
}

function formatEta(sec: number): string {
  if (sec < 30) return "now";
  const min = Math.max(1, Math.round(sec / 60));
  return `${min} min`;
}

function formatClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function keyOf(s: StationOption): string {
  return `${s.routeId}::${s.stop.id}`;
}

function routeShortFor(routeId: string | undefined): string | null {
  if (routeId === "40_100479") return "1";
  if (routeId === "40_2LINE") return "2";
  return null;
}

function groupedOptions(stations: StationOption[]): {
  routeId: string;
  label: string;
  options: StationOption[];
}[] {
  const byRoute = new Map<
    string,
    { routeId: string; label: string; options: StationOption[] }
  >();
  for (const s of stations) {
    const existing = byRoute.get(s.routeId);
    if (existing) {
      existing.options.push(s);
    } else {
      byRoute.set(s.routeId, {
        routeId: s.routeId,
        label: `${s.routeShortName} Line`,
        options: [s],
      });
    }
  }
  return Array.from(byRoute.values());
}
